import api from 'api';
import { Prompt } from 'components/Base';
import SwitchPoundPercantage from 'components/Fields/SwitchPoundPercantage/SwitchPoundPercantage';
import Icons from 'components/Icons';
import TextInput from 'components/TextInput/TextInput';
import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  formatCurrency,
  formatPercent,
  Notify,
  unFormatCurrency,
  unFormatPercent
} from 'utils';
import { investmentType } from '../types';
import { chargeTypeEnum } from './CipThreshold';
import ModelPortfolioSelector from './ModelPortfolioSelector';

type InvestmentProps = {
  investment: investmentType;
  deleteInvestment: Function;
  isDeleting: boolean;
  investments?: investmentType[];
  addModelPortfolioSelection?: Function;
  type: 'fund' | 'model portfolio' | 'custom model portfolio';
  isFirmAdmin: boolean;
};

const Investment: React.FC<InvestmentProps> = ({
  investment,
  deleteInvestment,
  isDeleting,
  investments = [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addModelPortfolioSelection = () => {},
  type,
  isFirmAdmin
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [chargeType, setChargeType] = useState(
    !!investment.mps_charge && investment.mps_charge.charge_type
      ? investment.mps_charge.charge_type
      : chargeTypeEnum.PERCENTAGE
  );
  const [chargeValue, setChargeValue] = useState(
    `${
      investment.mps_charge
        ? chargeType === chargeTypeEnum.PERCENTAGE
          ? investment.mps_charge.charge * 100
          : investment.mps_charge.charge
        : 0
    }`
  );
  const [deletePrompt, setDeletePrompt] = useState(false);
  const [editableInvestment, setEditableInvestment] = useState(investment);

  useEffect(() => {
    setEditableInvestment(investment);
  }, [isEditOpen]);

  const switchChargeType = () => {
    if (chargeType === chargeTypeEnum.PERCENTAGE) {
      setChargeType(chargeTypeEnum.MONETARY);
    } else {
      setChargeType(chargeTypeEnum.PERCENTAGE);
    }
  };

  const updateMpsCharge = useDebouncedCallback(
    (charge, modelPortfolioId, name) => {
      setChargeValue(charge);
      api
        .addOrUpdateModelPortfolioCharge({
          modelPortfolioId,
          chargeData: {
            charge:
              chargeType === chargeTypeEnum.MONETARY
                ? Number(unFormatCurrency(charge))
                : Number(unFormatPercent(charge) / 100),
            charge_type: chargeType
          }
        })
        .then(() => {
          Notify.success(`Charge for ${name} has been updated to ${charge}`);
        });
    },
    1000
  );

  const nameColWidth = {
    fund: '90%',
    'model portfolio': '60%',
    'custom model portfolio': '60%'
  }[type];

  return (
    <div className="investmentRow">
      <div style={{ width: nameColWidth }}>{investment.name}</div>
      {type !== 'fund' && (
        <div style={{ width: type === 'model portfolio' ? '30%' : '20%' }}>
          <div className="chargeTypeSwitch">
            {isFirmAdmin ? (
              <>
                <TextInput
                  label=""
                  value={
                    chargeType === chargeTypeEnum.PERCENTAGE
                      ? formatPercent(chargeValue)
                      : formatCurrency(chargeValue)
                  }
                  onChange={(e: string) =>
                    updateMpsCharge(e, investment.mp_id, investment.name)
                  }
                />
                <div onClick={switchChargeType} style={{ marginTop: '10px' }}>
                  <SwitchPoundPercantage
                    input={{ value: chargeType === chargeTypeEnum.PERCENTAGE }}
                  />
                </div>
              </>
            ) : (
              <>
                {chargeType === chargeTypeEnum.PERCENTAGE
                  ? formatPercent(chargeValue)
                  : formatCurrency(chargeValue)}
              </>
            )}
          </div>
        </div>
      )}
      {type === 'custom model portfolio' && isFirmAdmin && (
        <div style={{ width: '10%', cursor: 'pointer' }}>
          <Icons.Edit onClick={() => setIsEditOpen(true)} />
        </div>
      )}
      {isFirmAdmin && (
        <div
          style={{ width: '10%', cursor: 'pointer', textAlign: 'right' }}
          onClick={() => setDeletePrompt(true)}
        >
          <Icons.DeleteIcon />
        </div>
      )}
      <Prompt
        isOpen={deletePrompt}
        title={`Are you sure you want to delete this investment?`}
        confirmAction={() => deleteInvestment(investment)}
        isProcessing={isDeleting}
        cancelAction={() => setDeletePrompt(false)}
      />
      {isEditOpen && (
        <ModelPortfolioSelector
          modelPortfolioId={editableInvestment.id}
          date={editableInvestment.last_updated}
          isOpen={isEditOpen}
          closeModelFunc={() => setIsEditOpen(false)}
          investments={investments}
          onClone={() =>
            setEditableInvestment({ ...editableInvestment, id: undefined })
          }
          addModelPortfolioSelection={addModelPortfolioSelection}
          option={'create'}
        />
      )}
    </div>
  );
};

export default Investment;
